import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { RachaPanel } from '../components/RachaPanel';
import PredictionPaymentModal from '../components/PredictionPaymentModal';
import { toast } from 'sonner';
import {
  Zap, Flame, Snowflake, Clock, Target, TrendingUp, Shield,
  Loader2, ChevronDown, ChevronUp, BarChart3, Lock, Unlock,
  ArrowLeft, Star, Terminal, Search, Timer, Sparkles, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const ScoreBadge = ({ score }) => {
  let color = 'text-slate-400 bg-slate-500/15 border-slate-700';
  if (score >= 70) color = 'text-green-400 bg-green-500/15 border-green-500/30';
  else if (score >= 50) color = 'text-amber-400 bg-amber-500/15 border-amber-500/30';
  return <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border ${color}`}>{score}</span>;
};

    const NumberOrb = ({ num, score, rank, isHot, isCold, isRebote, momentum, size = 'md' }) => {
  const s = size === 'lg' ? 'w-14 h-14 text-xl' : 'w-11 h-11 text-base';
  const RANK_GRADIENTS = [
    'from-amber-400 to-orange-500 shadow-amber-500/30',
    'from-green-400 to-emerald-500 shadow-green-500/20',
    'from-cyan-400 to-blue-500 shadow-blue-500/20',
  ];
  const gradient = RANK_GRADIENTS[rank] || 'from-slate-400 to-slate-500 shadow-slate-500/10';

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${s} rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-black shadow-lg relative`}>
        {String(num).padStart(2, '0')}
        {isHot && <Flame size={10} className="absolute -top-1 -right-1 text-red-400" />}
        {isCold && <Snowflake size={10} className="absolute -top-1 -right-1 text-blue-400" />}
        {isRebote && <TrendingUp size={10} className="absolute -top-1 -right-1 text-amber-400" />}
        {momentum === 'rising' && !isRebote && !isHot && !isCold && (
          <TrendingUp size={10} className="absolute -top-1 -right-1 text-green-400" />
        )}
      </div>
      <ScoreBadge score={score} />
    </div>
  );
};

const PairCard = ({ pair, score, coOccurrences }) => (
  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/30">
    <div className="flex gap-1.5">
      <span className="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-300 font-bold text-sm">
        {String(pair[0]).padStart(2, '0')}
      </span>
      <span className="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-300 font-bold text-sm">
        {String(pair[1]).padStart(2, '0')}
      </span>
    </div>
    <div className="flex-1">
      <div className="flex items-center justify-between">
        <span className="text-slate-400 text-[10px]">{coOccurrences}x juntos</span>
        <ScoreBadge score={score} />
      </div>
    </div>
  </div>
);

const CountdownTimer = ({ drawTime }) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const [h, m] = drawTime.split(':').map(Number);
      const target = new Date(now);
      target.setHours(h, m, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      const diff = target - now;
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${hrs}h ${mins}m`);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [drawTime]);
  return (
    <span className="flex items-center gap-1 text-xs text-slate-500">
      <Timer size={12} />
      {remaining}
    </span>
  );
};

const LotteryCard = ({ prediction, isPremium, expanded, onToggle }) => {
  const conf = prediction.confidence || 0;
  const confColor = conf >= 80 ? 'text-green-400' : (conf >= 60 ? 'text-amber-400' : 'text-red-400');

  return (
    <Card
      className="bg-slate-900/70 border-slate-800/60 overflow-hidden hover:border-slate-700/60 transition-colors"
      data-testid={`7ia-lottery-${prediction.lottery_id}`}
    >
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="w-3 h-10 rounded-full flex-shrink-0"
            style={{ backgroundColor: prediction.color || '#22c55e' }}
          />
          <div className="min-w-0">
            <h3 className="text-white font-bold text-sm truncate">{prediction.lottery_name}</h3>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-xs">{prediction.draw_time}</span>
              <CountdownTimer drawTime={prediction.draw_time} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex gap-1.5">
            {prediction.top_numbers?.slice(0, isPremium ? 5 : 3).map((n, i) => (
              <NumberOrb key={`orb-${n.number}`} num={n.number} score={n.score} rank={i} isHot={n.is_hot} isCold={n.is_cold} isRebote={n.is_rebote} momentum={n.momentum} />
            ))}
            {!isPremium && prediction.top_numbers?.length > 3 && (
              <div className="w-11 h-11 rounded-full bg-slate-800 border border-dashed border-slate-600 flex items-center justify-center">
                <Lock size={14} className="text-slate-600" />
              </div>
            )}
          </div>
          <div className="text-center min-w-[50px]">
            <p className={`text-xl font-black ${confColor}`}>{conf}%</p>
            <p className="text-slate-600 text-[9px]">CONF</p>
          </div>
          {expanded ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-600" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/60 pt-3 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Score breakdown for top numbers */}
          <div className={`grid gap-2 ${isPremium ? 'grid-cols-5' : 'grid-cols-3'}`}>
            {prediction.top_numbers?.slice(0, isPremium ? 5 : 3).map((n, i) => (
              <div key={`rec-${n.number}`} className="p-2 rounded-lg bg-slate-800/30 text-center">
                <p className="text-white font-bold text-lg">{String(n.number).padStart(2, '0')}</p>
                {n.momentum && (
                  <Badge className={`text-[8px] mb-1 px-1 py-0 ${
                    n.momentum === 'rising' ? 'bg-green-500/15 text-green-400 border-green-500/30'
                    : n.momentum === 'falling' ? 'bg-red-500/15 text-red-400 border-red-500/30'
                    : 'bg-slate-500/15 text-slate-400 border-slate-600'
                  }`}>
                    {n.momentum === 'rising' ? 'EN ALZA' : n.momentum === 'falling' ? 'BAJANDO' : 'ESTABLE'}
                  </Badge>
                )}
                <div className="grid grid-cols-1 gap-0.5 mt-1 text-[9px]">
                  {n.breakdown && Object.entries(n.breakdown).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-600">{k === 'freq_trend' ? 'frec' : k === 'position' ? 'pos' : k}</span>
                      <span className="text-slate-400">{v}</span>
                    </div>
                  ))}
                  {n.overdue > 1.2 && (
                    <div className="flex justify-between text-amber-400">
                      <span>vencido</span>
                      <span>{n.overdue}x</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Rising Stars + Rebotes (V2) */}
          <div className="grid grid-cols-2 gap-2">
            {prediction.rising_stars?.length > 0 && (
              <div className="p-2 rounded-lg bg-slate-800/30">
                <p className="text-green-400 text-[10px] font-bold mb-1 flex items-center gap-1"><TrendingUp size={10} />EN ALZA</p>
                <div className="flex flex-wrap gap-1">
                  {prediction.rising_stars.slice(0, 4).map((r) => (
                    <span key={r.number} className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-[10px] font-mono">
                      {r.number} <span className="text-green-600">{r.momentum_ratio}x</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {prediction.rebote_candidates?.length > 0 && (
              <div className="p-2 rounded-lg bg-slate-800/30">
                <p className="text-amber-400 text-[10px] font-bold mb-1 flex items-center gap-1"><Zap size={10} />REBOTE</p>
                <div className="flex flex-wrap gap-1">
                  {prediction.rebote_candidates.slice(0, 4).map((r) => (
                    <span key={r.number} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px] font-mono">
                      {r.number} <span className="text-amber-600">{r.overdue}x</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pales - premium only */}
          {isPremium && prediction.top_pairs?.length > 0 && (
            <div>
              <p className="text-slate-500 text-[10px] font-bold mb-1.5 uppercase tracking-wider">Top Pales</p>
              <div className="grid grid-cols-3 gap-2">
                {prediction.top_pairs.map((p, i) => (
                  <PairCard key={`pair-${p.pair}`} pair={p.pair} score={p.score} coOccurrences={p.co_occurrences} />
                ))}
              </div>
            </div>
          )}
          {!isPremium && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <Lock size={14} className="text-amber-400" />
              <span className="text-amber-400 text-xs">Desbloquea Top 5 + Pales + Patrones con el analisis Premium</span>
            </div>
          )}

          {/* Hot / Cold */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded-lg bg-slate-800/30">
              <p className="text-red-400 text-[10px] font-bold mb-1 flex items-center gap-1"><Flame size={10} />CALIENTES</p>
              <div className="flex flex-wrap gap-1">
                {prediction.hot_numbers?.slice(0, 5).map((h) => (
                  <span key={h.number} className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-[10px] font-mono">
                    {h.number}{h.momentum === 'rising' ? '+' : ''}
                  </span>
                ))}
              </div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/30">
              <p className="text-blue-400 text-[10px] font-bold mb-1 flex items-center gap-1"><Snowflake size={10} />FRIOS</p>
              <div className="flex flex-wrap gap-1">
                {prediction.cold_numbers?.slice(0, 5).map((c) => (
                  <span key={c.number} className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[10px] font-mono">
                    {c.number}({c.draws_since}d){c.is_rebote ? '!' : ''}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Last Draw */}
          {prediction.last_draw?.length > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/20">
              <span className="text-slate-600 text-[10px]">Ultimo sorteo:</span>
              <div className="flex gap-1">
                {prediction.last_draw.map((n, i) => (
                  <span key={`ld-${n}-${i}`} className="px-1.5 py-0.5 rounded bg-slate-700/30 text-slate-500 text-[10px] font-mono">
                    {String(n).padStart(2, '0')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const SevenIAPage = () => {
  const { token, user } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [aiTips, setAiTips] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cmdInput, setCmdInput] = useState('');
  const cmdRef = useRef(null);

  const loadFree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/7ia/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPredictions(res.data.predictions || []);
      setIsPremium(false);
      setAiTips(null);
    } catch (e) {
      toast.error('Error al cargar predicciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadFree(); }, [loadFree]);

  const loadPremium = async () => {
    setPremiumLoading(true);
    try {
      const res = await axios.post(`${API}/7ia/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPredictions(res.data.predictions || []);
      setIsPremium(true);
      setAiTips(res.data.ai_tips);
      toast.success(`Analisis premium: ${res.data.count} loterias`);
    } catch (e) {
      if (e.response?.status === 402) {
        setShowPayment(true);
      } else {
        toast.error(e.response?.data?.detail || 'Error en analisis premium');
      }
    } finally {
      setPremiumLoading(false);
    }
  };

  const handleCommand = (cmd) => {
    const c = cmd.trim().toLowerCase();
    if (c === '/hoy' || c === 'hoy') {
      loadFree();
      toast.info('Cargando numeros del dia...');
    } else if (c === '/premium' || c === 'premium') {
      loadPremium();
    } else if (c.startsWith('/loteria') || c.startsWith('loteria')) {
      const name = c.replace('/loteria', '').replace('loteria', '').trim();
      if (name) {
        setSearchTerm(name);
        toast.info(`Filtrando: ${name}`);
      } else {
        setSearchTerm('');
        toast.info('Mostrando todas las loterias');
      }
    } else {
      toast.error('Comandos: /hoy, /loteria [nombre], /premium');
    }
    setCmdInput('');
  };

  const filtered = predictions.filter(p =>
    !searchTerm || p.lottery_name.toLowerCase().includes(searchTerm.toLowerCase())
      || p.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-6 pt-20 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-400 transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Zap size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight" data-testid="7ia-title">7IA</h1>
              <p className="text-slate-600 text-xs">Super Master Intelligence System</p>
            </div>
            <Badge className={isPremium
              ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30 ml-2'
              : 'bg-slate-700/50 text-slate-400 border-slate-600 ml-2'
            }>
              {isPremium ? 'PREMIUM' : 'FREE'}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {!isPremium && (
              <Button
                onClick={loadPremium}
                disabled={premiumLoading}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-sm px-5 py-2 rounded-lg shadow-lg shadow-cyan-500/15"
                data-testid="7ia-premium-btn"
              >
                {premiumLoading
                  ? <Loader2 size={16} className="animate-spin mr-1.5" />
                  : <Unlock size={16} className="mr-1.5" />
                }
                Premium ($25)
              </Button>
            )}
            {isPremium && (
              <Badge className="bg-green-500/15 text-green-400 border-green-500/30 px-3 py-1.5">
                <Sparkles size={12} className="mr-1" /> Top 5 + Pales + Sócrates
              </Badge>
            )}
          </div>
        </div>

        <RachaPanel engine="seven_ia" token={token} />

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar loteria..."
            className="pl-9 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-700 h-9 text-sm"
            data-testid="7ia-search"
          />
        </div>

        {/* AI Tips bar */}
        {aiTips?.general && (
          <div className="mb-4 p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/15 flex items-start gap-2" data-testid="7ia-ai-tips">
            <Sparkles size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
            <p className="text-cyan-300 text-xs leading-relaxed">{aiTips.general}</p>
          </div>
        )}

        {/* Stats bar */}
        {!loading && (
          <div className="flex items-center gap-4 mb-4 text-xs text-slate-600">
            <span>{filtered.length} loterias</span>
            <span>Modo: {isPremium ? 'Premium' : 'Free'}</span>
            {isPremium && <span className="text-cyan-500">Top 5 + Pales desbloqueados</span>}
          </div>
        )}

        {/* Loading */}
        {(loading || premiumLoading) && (
          <div className="flex flex-col items-center py-20" data-testid="7ia-loading">
            <div className="relative mb-4">
              <div className="w-16 h-16 rounded-full border-2 border-cyan-500/20 border-t-cyan-500 animate-spin" />
              <Zap size={20} className="text-cyan-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-slate-400 text-sm">{premiumLoading ? 'Generando analisis premium con Sócrates...' : 'Calculando scores...'}</p>
          </div>
        )}

        {/* Lottery Grid */}
        {!loading && !premiumLoading && (
          <div className="space-y-2" data-testid="7ia-results">
            {filtered.map((p, idx) => (
              <LotteryCard
                key={p.lottery_id}
                prediction={p}
                isPremium={isPremium}
                expanded={expandedIdx === idx}
                onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <p className="text-slate-600 text-sm">No se encontraron loterias</p>
              </div>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-6 p-3 rounded-lg bg-slate-900/30 border border-slate-800/30 flex items-start gap-2">
          <Shield size={14} className="text-slate-700 flex-shrink-0 mt-0.5" />
          <p className="text-slate-700 text-[10px] leading-relaxed">
            7IA usa analisis estadistico avanzado. Los resultados de loteria son aleatorios. Juega responsablemente.
          </p>
        </div>
      </main>

      {/* Command Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0e1a]/95 backdrop-blur-xl border-t border-slate-800/50 p-3 z-40">
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          <Terminal size={16} className="text-slate-600 flex-shrink-0" />
          <Input
            ref={cmdRef}
            value={cmdInput}
            onChange={(e) => setCmdInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCommand(cmdInput); }}
            placeholder="/hoy · /loteria [nombre] · /premium"
            className="bg-slate-900/50 border-slate-800 text-cyan-300 placeholder:text-slate-700 h-9 text-sm font-mono"
            data-testid="7ia-command-input"
          />
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-600 hover:text-cyan-400 px-2"
            onClick={() => handleCommand(cmdInput)}
            data-testid="7ia-command-submit"
          >
            <Zap size={16} />
          </Button>
        </div>
      </div>

      <PredictionPaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={() => { setShowPayment(false); loadPremium(); }}
      />
    </div>
  );
};

export default SevenIAPage;
